import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { of, type Observable } from 'rxjs';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { MailProcessing } from '../db/MailExtractionResult.js';

/**
 * A live "this request changed on the server" signal for one request.
 *
 * The detail view keeps the extraction in an ordinary query, because it is an entity the user
 * edits and saves — a subscription writing into it mid-edit would fight the person typing. But
 * the extraction is no longer only changed by the person looking at it: answering a question,
 * re-reading a mail reply, and a colleague's correction all rewrite it from the server, and
 * until now the only way to see any of that was reloading the page.
 *
 * So the pattern already used for `subscribeLastIntrospectNodeByOperationAndParam` is extended
 * to those paths: the subscription carries no content, only the timestamps that move whenever
 * the request was touched, and the view uses them to re-run its own query. `validatedAt` is the
 * right marker because every server path that rewrites an extraction revalidates it.
 */
export const subscribeRequestChanges = registerSubscription({
  type: () => [MailProcessing],
  operationName: 'subscribeRequestChanges',
  resolve: (mailID: string) => {
    return (_gqlFields: BaseQLSelectionSet<MailProcessing>): Observable<MailProcessing[]> => {
      if (!getCurrentUserID()) return of([]);
      return watchQuery<MailProcessing>(MailProcessing._collectionName, { mailID }, { limit: 1 });
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});
